<template>
  <div class="card-body px-0 pb-2 pe-3">
        <div class="row justify-content-center ms-3">
          <div class="col-12">
        <form @submit.prevent="handleSubmit" @keydown.enter.prevent>
          <div class="row g-3">
            <!-- Subject Name -->
            <div class="col-md-6">
              <label for="name" class="bmd-label-float">{{ $t('subjects.name') }}</label>
              <input
                type="text"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.name }"
                id="name"
                v-model.trim="formData.name"
                required
                @blur="validateField('name')"
              />
              <div class="invalid-feedback">{{ errors.name }}</div>
            </div>

            <!-- Subject Code -->
            <div class="col-md-6">
              <label for="code" class="bmd-label-float">{{ $t('subjects.code') }}</label>
              <input
                type="text"
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.code }"
                id="code"
                v-model.trim="formData.code"
                required
                @blur="validateField('code')"
              />
              <div class="invalid-feedback">{{ errors.code }}</div>
            </div>

            <!-- Description -->
            <div class="col-12">
              <label for="description" class="bmd-label-float">{{ $t('subjects.description') }}</label>
              <textarea
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.description }"
                id="description"
                v-model.trim="formData.description"
                rows="3"
              ></textarea>
              <div class="invalid-feedback">{{ errors.description }}</div>
            </div>

            <!-- Class -->
            <div class="col-md-6">
              <label for="class_id" class="bmd-label-float">{{ $t('subjects.class') }}</label>
              <select
                class="form-select borderless-input"
                :class="{ 'is-invalid': errors.class_id }"
                id="class_id"
                v-model="formData.class_id"
                required
                @blur="validateField('class_id')"
              >
                <option value="" disabled>{{ $t('subjects.selectClass') }}</option>
                <option v-for="classItem in classes" :key="classItem.id" :value="classItem.id">
                  {{ classItem.name }}
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.class_id }}</div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, defineEmits, defineProps, defineExpose } from "vue";
import { useToast } from "vue-toastification";
import api from "@/api";
import { useI18n } from 'vue-i18n';
const { t } = useI18n();

const props = defineProps({
  title: String,
  submitText: {
    type: String,
    default: "subjects.save"
  },
  loading: {
    type: Boolean,
    default: false
  },
  isEditMode: {
    type: Boolean,
    default: false
  },
  initialData: {
    type: Object,
    default: () => ({
      name: '',
      code: '',
      description: '',
      class_id: null
    })
  }
});

const emit = defineEmits(['submit', 'cancel', 'validation-error', 'update:form-data']);
const toast = useToast();

// Form data and state
const formData = ref({ ...props.initialData });
const errors = ref({});
const classes = ref([]);

// Validation functions
const validateField = (field) => {
  const REQUIRED_MSG = t('validation.required') || 'This field is required';

  if (!formData.value[field] && field !== 'description') {
    errors.value[field] = REQUIRED_MSG;   // localized message
    return false;
  }
  delete errors.value[field];
  return true;
};


const validateForm = () => {
  errors.value = {};
  const validations = [
    validateField('name'),
    validateField('code'),
    validateField('class_id')
  ];

  return validations.every(valid => valid);
};

const setErrors = (serverErrors) => {
  errors.value = { ...serverErrors };
};

// Watch for initialData changes
watch(() => props.initialData, (newData) => {
  formData.value = { ...newData };
}, { immediate: true, deep: true });

onMounted(async () => {
  try {
const response = await api.get('/classes', {
  params: {
    all: true
  }
});    classes.value = response.data.data;
  } catch (error) {
    console.error("Error fetching classes:", error);
  }
});

const handleSubmit = () => {
  if (!validateForm()) {
    emit('validation-error');
    toast.warning(toast.$t ? toast.$t('validation.correctErrors') : "Please correct the errors in the form");
    return;
  }
  emit('submit', formData.value);
};

defineExpose({
  submitForm: handleSubmit, 
  setErrors
});

watch(formData, (newVal) => {
  emit('update:form-data', newVal);
}, { deep: true });
</script>
